/**
 * 動画ファイルの受け入れ判定(#55 の実運用で判明)。
 *
 * 実際に選べなかった例:
 *   DJI_20260707143244_0056_D.MP4 のコピー
 *
 * ファイル名の末尾が「のコピー」で拡張子が失われており、
 * `accept="video/*"` はブラウザが拡張子から MIME を引けないファイルを
 * グレーアウトする。**クラウド同期やコピーで接尾辞が付くのはよくあること**なので、
 * 現場で普通に起きる。
 *
 * さらに仮に選べても、Storage ルールが contentType を video/* に限っているため、
 * `file.type` が空だとアップロード自体が弾かれる。**2段で失敗していた。**
 */

/** 拡張子 → MIME。`accept` とアップロード時の contentType で共用する。 */
const VIDEO_MIME: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  qt: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  // DJI のプロキシ。中身は H.264 の MP4 なので Gemini も ffmpeg も読める
  lrf: "video/mp4",
};

/**
 * ファイル選択ダイアログの accept 値。
 * `video/*` だけだと拡張子を失ったファイルが選べないため、拡張子も併記する。
 * 大文字の `.MP4` も明示する(環境によって大小を区別する)。
 */
export const VIDEO_ACCEPT = [
  "video/*",
  ...Object.keys(VIDEO_MIME).flatMap((e) => [`.${e}`, `.${e.toUpperCase()}`]),
].join(",");

/** 「…MP4 のコピー」のように末尾に語が付いていても拾えるよう、名前全体から探す。 */
function extFromName(name: string): string | null {
  const lower = name.toLowerCase();
  for (const ext of Object.keys(VIDEO_MIME)) {
    // 「.mp4」が末尾か、直後が英数字以外(空白・括弧・日本語)ならその拡張子とみなす
    const re = new RegExp(`\\.${ext}(?![0-9a-z])`, "i");
    if (re.test(lower)) return ext;
  }
  return null;
}

/**
 * アップロード時に渡す contentType を決める。
 *
 * ブラウザが判定できていればそれを使い、できなければ拡張子から補う。
 * どちらも駄目なら null を返し、呼び出し側で理由を出して止める
 * (勝手に video/mp4 と決めつけると、動画でないものが上がる)。
 */
export function videoContentType(file: File): string | null {
  if (file.type && file.type.startsWith("video/")) return file.type;
  const ext = extFromName(file.name);
  return ext ? VIDEO_MIME[ext] : null;
}

/** 選べてしまったが動画と判定できないファイルへの説明。 */
export const VIDEO_TYPE_ERROR =
  "動画として認識できませんでした。ファイル名の末尾が拡張子(.mp4 / .mov など)で終わっているか確認してください。" +
  "クラウド同期やコピーで「のコピー」などが付いていると認識できません。";
